import { useCallback, useEffect } from "react";

import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { reorderWithEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge";

import { DRAGGABLE_COMBOBOX_CHIP_EVENT_TYPE } from "../constants";
import type {
  DraggableComboboxChipMetaData,
  SearchMultiSelectItem,
} from "../types";

type UseDraggableFieldsParams<
  Value extends SearchMultiSelectItem,
  FieldName extends string,
> = {
  fieldValues: { [key in FieldName]: Value[] };
  setFieldValues: (values: { [key in FieldName]: Value[] }) => void;
};

/**
 * Utils for managing dragging within single field / across multiple fields
 */
export function useDraggableFields<
  Value extends SearchMultiSelectItem,
  FieldName extends string,
>({ fieldValues, setFieldValues }: UseDraggableFieldsParams<Value, FieldName>) {
  const fieldNames = Object.keys(fieldValues) as FieldName[];

  const handleSelectionChange = useCallback(
    (fieldToChange: FieldName, newItems: Value[]) => {
      const newItemIds = new Set(newItems.map((item) => item.id));

      const updatedValues = { ...fieldValues };

      // Update the changed field with new items
      updatedValues[fieldToChange] = newItems;

      // Remove selected items from all other fields to prevent duplicates
      fieldNames.forEach((fieldName) => {
        if (fieldName !== fieldToChange) {
          updatedValues[fieldName] = fieldValues[fieldName].filter(
            (item) => !newItemIds.has(item.id),
          );
        }
      });

      setFieldValues(updatedValues);
    },
    [fieldNames, fieldValues, setFieldValues],
  );

  // Monitoring dragging events
  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) =>
          source.data.eventType === DRAGGABLE_COMBOBOX_CHIP_EVENT_TYPE,
        onDrop: ({ source, location }) => {
          // Guard against empty drop targets
          if (location.current.dropTargets.length === 0) return;

          const target = location.current.dropTargets[0];

          const sourceData = source.data as DraggableComboboxChipMetaData<
            Value,
            FieldName
          >;
          const targetData = target.data as DraggableComboboxChipMetaData<
            Value,
            FieldName
          >;

          const sourceFieldName = sourceData.fieldName;
          const targetFieldName = targetData.fieldName;

          const closestEdgeOfTarget = extractClosestEdge(targetData);

          const isDroppingIntoSameField = sourceFieldName === targetFieldName;

          const newItems = reorderWithEdge({
            list: isDroppingIntoSameField
              ? fieldValues[targetFieldName]
              : // Dropping across fields requires appending the source item to the current target field value before reordering
                fieldValues[targetFieldName].concat(sourceData.item),
            startIndex: isDroppingIntoSameField
              ? fieldValues[sourceFieldName].indexOf(sourceData.item)
              : // Dropping across fields means the source item is always the last item (the one just appended above)
                fieldValues[targetFieldName].length,
            indexOfTarget: fieldValues[targetFieldName].indexOf(
              targetData.item,
            ),
            closestEdgeOfTarget: targetData.isDropableInput
              ? "right"
              : closestEdgeOfTarget,
            axis: "horizontal",
          });

          handleSelectionChange(targetFieldName, newItems);
        },
      }),
    [handleSelectionChange, fieldValues, fieldNames],
  );

  return { handleSelectionChange };
}
